import { useMemo } from 'react';

// === DICCIONARIO DE MEDALLAS (CATEGORÍAS Y NIVELES) ===
const BADGES_DICTIONARY = [
  {
    id: 'streak',
    baseIcon: '🔥',
    categoryName: 'Fuego Inextinguible',
    description: 'Días seguidos estudiando',
    tiers: [
      { requirement: 3,   title: 'Chispa',     gradient: 'from-orange-200 to-orange-300', shadow: 'shadow-orange-200' },
      { requirement: 7,   title: 'Llama',      gradient: 'from-orange-400 to-red-400',    shadow: 'shadow-red-200' },
      { requirement: 30,  title: 'Hoguera',    gradient: 'from-red-500 to-rose-600',      shadow: 'shadow-rose-300' },
      { requirement: 100, title: 'Incendio',   gradient: 'from-rose-600 to-purple-600',   shadow: 'shadow-purple-300' },
      { requirement: 365, title: 'Sol Eterno', gradient: 'from-yellow-400 via-red-500 to-purple-600', shadow: 'shadow-yellow-300' }
    ],
    // Extrae la racha máxima alcanzada
    getValue: (progress) => progress?.maxStreak || 0
  },
  {
    id: 'sets_completed',
    baseIcon: '📚',
    categoryName: 'Devorador de Libros',
    description: 'Sets de ejercicios terminados',
    tiers: [
      { requirement: 10,   title: 'Lector',     gradient: 'from-green-200 to-emerald-300', shadow: 'shadow-green-200' },
      { requirement: 50,   title: 'Estudiante', gradient: 'from-emerald-400 to-teal-400', shadow: 'shadow-emerald-200' },
      { requirement: 250,  title: 'Erudito',    gradient: 'from-teal-500 to-cyan-500',     shadow: 'shadow-cyan-200' },
      { requirement: 1000, title: 'Sabio',      gradient: 'from-cyan-600 to-blue-600',     shadow: 'shadow-blue-300' },
      { requirement: 2000, title: 'Biblioteca', gradient: 'from-blue-600 to-indigo-700',   shadow: 'shadow-indigo-400' }
    ],
    // Cuenta la cantidad de sets en el array de completados
    getValue: (progress) => progress?.completedSets?.length || 0
  },
  {
    id: 'lifetime_xp',
    baseIcon: '⚡',
    categoryName: 'Poder Puro',
    description: 'Experiencia histórica acumulada',
    tiers: [
      { requirement: 5000,   title: 'Chispa',    gradient: 'from-blue-200 to-indigo-300',   shadow: 'shadow-blue-200' },
      { requirement: 50000,  title: 'Rayo',      gradient: 'from-indigo-400 to-purple-400', shadow: 'shadow-indigo-200' },
      { requirement: 100000, title: 'Tormenta',  gradient: 'from-purple-500 to-fuchsia-500',shadow: 'shadow-purple-300' },
      { requirement: 250000, title: 'Ciclón',    gradient: 'from-fuchsia-600 to-pink-600',  shadow: 'shadow-pink-300' },
      { requirement: 400000, title: 'Supernova', gradient: 'from-violet-600 via-fuchsia-600 to-orange-500', shadow: 'shadow-fuchsia-400' }
    ],
    getValue: (progress) => Number(progress?.lifetimeXp || progress?.xp || 0)
  },
  {
    id: 'perfect_sets',
    baseIcon: '🎯',
    categoryName: 'Francotirador',
    description: 'Sets completados sin errores',
    tiers: [
      { requirement: 1,   title: 'Buen Ojo',    gradient: 'from-slate-300 to-slate-400', shadow: 'shadow-slate-200' }, // Bronce
      { requirement: 10,  title: 'Certero',     gradient: 'from-slate-400 to-slate-600', shadow: 'shadow-slate-300' }, // Plata
      { requirement: 50,  title: 'Impecable',   gradient: 'from-yellow-300 to-amber-500',shadow: 'shadow-yellow-200' }, // Oro
      { requirement: 100, title: 'Asesino',     gradient: 'from-cyan-300 to-blue-400',   shadow: 'shadow-cyan-200' }, // Diamante
      { requirement: 200, title: 'John Wick',   gradient: 'from-slate-800 to-black',     shadow: 'shadow-slate-600' } // Leyenda
    ],
    // Asume que guardas los sets perfectos. Si no, devuelve 0 por ahora hasta que lo implementemos.
    getValue: (progress) => progress?.perfectSetsCount || 0
  },
  {
    id: 'cefr_mastery',
    baseIcon: '🏆',
    categoryName: 'Dominio del Idioma',
    description: 'Nivel CEFR alcanzado',
    tiers: [
      { requirement: 1, title: 'A1 - Básico',    gradient: 'from-lime-300 to-green-400', shadow: 'shadow-lime-200' },
      { requirement: 2, title: 'A2 - Pre-Int',   gradient: 'from-green-400 to-emerald-500', shadow: 'shadow-emerald-200' },
      { requirement: 3, title: 'B1 - Intermedio',gradient: 'from-emerald-500 to-teal-600', shadow: 'shadow-teal-300' },
      { requirement: 4, title: 'B2 - Avanzado',  gradient: 'from-teal-600 to-cyan-700', shadow: 'shadow-cyan-300' },
      { requirement: 5, title: 'C1 - Fluido',    gradient: 'from-indigo-500 to-purple-700', shadow: 'shadow-indigo-400' }
    ],
    // Convertimos el string del nivel a un número para la lógica de tiers
    getValue: (progress) => {
      const levelMap = { 'A1': 1, 'A2': 2, 'B1': 3, 'B2': 4, 'C1': 5, 'C2': 5 };
      return levelMap[progress?.cefrLevel] || 0;
    }
  },
  {
    id: 'fashionista',
    baseIcon: '🛍️',
    categoryName: 'Coleccionista',
    description: 'Skins de mascotas desbloqueadas',
    tiers: [
      { requirement: 1,  title: 'Comprador', gradient: 'from-pink-200 to-pink-300', shadow: 'shadow-pink-200' },
      { requirement: 3,  title: 'A la Moda', gradient: 'from-pink-400 to-rose-400', shadow: 'shadow-pink-300' },
      { requirement: 5,  title: 'Estilista', gradient: 'from-rose-500 to-red-500',  shadow: 'shadow-rose-300' },
      { requirement: 10, title: 'Boutique',  gradient: 'from-red-600 to-fuchsia-600', shadow: 'shadow-red-400' },
      { requirement: 15, title: 'Ícono',     gradient: 'from-fuchsia-500 via-pink-500 to-rose-500', shadow: 'shadow-fuchsia-300' }
    ],
    // Cuenta cuántas skins tiene en su array de skins compradas
    getValue: (progress) => progress?.unlockedSkins?.length || (progress?.currentSkin ? 1 : 0)
  },
  {
    id: 'time_invested',
    baseIcon: '⏳',
    categoryName: 'Dedicación',
    description: 'Horas totales de estudio',
    tiers: [
      { requirement: 1,   title: 'Un Buen Rato', gradient: 'from-sky-200 to-blue-300', shadow: 'shadow-sky-200' },
      { requirement: 10,  title: 'Constante',    gradient: 'from-blue-400 to-indigo-400', shadow: 'shadow-blue-300' },
      { requirement: 50,  title: 'Comprometido', gradient: 'from-indigo-500 to-violet-500', shadow: 'shadow-indigo-300' },
      { requirement: 100, title: 'Veterano',     gradient: 'from-violet-600 to-purple-700', shadow: 'shadow-violet-400' },
      { requirement: 500, title: 'Inmortal',     gradient: 'from-slate-700 via-purple-800 to-slate-900', shadow: 'shadow-slate-500' }
    ],
    // Asumimos que tienes studyMinutes. Lo dividimos entre 60 para horas.
    getValue: (progress) => Math.floor((progress?.studyMinutes || 0) / 60)
  }
];

export default function useBadges(userProgress) {
  const badgesData = useMemo(() => {
    if (!userProgress) return [];

    return BADGES_DICTIONARY.map(badgeCategory => {
      // 1. Obtenemos el valor actual del usuario para esta categoría (ej. 14 días de racha)
      const userValue = badgeCategory.getValue(userProgress);
      
      // 2. Filtramos los niveles que ya superó o igualó
      const unlockedTiers = badgeCategory.tiers.filter(t => userValue >= t.requirement);
      
      // 3. Identificamos el nivel actual (el más alto que ha desbloqueado)
      const currentTierIndex = unlockedTiers.length - 1;
      const currentTier = currentTierIndex >= 0 ? unlockedTiers[currentTierIndex] : null;
      
      // 4. Identificamos el siguiente nivel a desbloquear
      const nextTierIndex = currentTierIndex + 1;
      const nextTier = badgeCategory.tiers[nextTierIndex] || null;

      // 5. Calculamos el porcentaje hacia el siguiente nivel
      let progressToNext = 100; // Si ya llegó al máximo, es 100%
      let progressText = '¡Al máximo!';

      if (nextTier) {
        // Necesitamos saber desde qué base estamos calculando. 
        // Si está en el tier 1 (req 3) buscando el tier 2 (req 7), la base es 0 para porcentaje total, 
        // o podemos hacerlo simple: (valor / meta_siguiente) * 100
        progressToNext = Math.min((userValue / nextTier.requirement) * 100, 100);
        progressText = `${userValue} / ${nextTier.requirement}`;
      }

      // 6. Retornamos el objeto procesado listo para la UI
      return {
        id: badgeCategory.id,
        icon: badgeCategory.baseIcon,
        categoryName: badgeCategory.categoryName,
        description: badgeCategory.description,
        isUnlocked: unlockedTiers.length > 0, // True si al menos desbloqueó el tier 1
        
        // Datos visuales del tier actual (Si no tiene, damos valores por defecto en gris)
        currentTierName: currentTier ? currentTier.title : 'Bloqueado',
        currentGradient: currentTier ? currentTier.gradient : 'from-slate-100 to-slate-200',
        currentShadow: currentTier ? currentTier.shadow : 'shadow-none',
        
        // Datos matemáticos
        userValue: userValue,
        isMaxedOut: !nextTier,
        nextTierName: nextTier ? nextTier.title : null,
        progressToNext: progressToNext,
        progressText: progressText
      };
    });
  }, [userProgress]);

  return badgesData;
}